import React, { createContext, useContext, useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { api } from "../utils/api";

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastFetchedUserIdRef = useRef(null);
  const currentUserRef = useRef(null);

  const updateUser = useCallback((newUser) => {
    if (typeof newUser === 'function') {
      setUser(prevUser => {
        const updatedUser = newUser(prevUser);
        currentUserRef.current = updatedUser;
        return updatedUser;
      });
    } else {
      setUser(newUser);
      currentUserRef.current = newUser;
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    let refreshInterval;

    const unsubscribe = api.onSessionChange((event, session) => {
      if (!mounted) return;
      if (session?.user) {
        const current = currentUserRef.current;
        // Refresh events carry the same identity without the profile — don't
        // clobber an already-loaded profile. When the profile is missing (e.g.
        // OAuth callback sets the session after init ran), load it here.
        const needsProfile = !current || current.id !== session.user.id || !current.profile;
        if (needsProfile) {
          updateUser(session.user);
          loadUserProfile(session.user).catch(() => {});
        }
      } else {
        updateUser(null);
      }
    });

    // Get initial session (api.getSession now auto-refreshes expired tokens)
    const init = async () => {
      try {
        const { data: { session }, error } = await api.getSession();
        if (!mounted) return;

        if (error || !session?.user) {
          updateUser(null);
          setLoading(false);
          return;
        }

        updateUser(session.user);
        setLoading(false);
        loadUserProfile(session.user).catch(() => {});
      } catch (error) {
        console.error('Error getting session:', error);
        if (mounted) {
          updateUser(null);
          setLoading(false);
        }
      }
    };

    init();

    // Periodic check as a fallback (api.call and api.getSession also auto-refresh)
    refreshInterval = setInterval(async () => {
      if (!mounted) return;
      const { data: { session } } = await api.getSession();
      if (!mounted) return;
      if (!session?.user) {
        updateUser(null);
      }
    }, 50 * 60 * 1000);

    return () => {
      mounted = false;
      clearInterval(refreshInterval);
      unsubscribe();
    };
  }, [updateUser]);

  const loadUserProfile = async (authUser, attempt = 1) => {
    if (lastFetchedUserIdRef.current === authUser.id) {
      console.log('User profile already fetched for this user, skipping');
      return;
    }

    try {
      // console.log('Loading user profile for:', authUser.id);
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Profile fetch timeout')), 10000); // 10 second timeout
      });

      const profilePromise = api.getProfile(authUser.id);
      let { data: profile, error } = await Promise.race([profilePromise, timeoutPromise]);

      if (error) {
        console.error('Profile fetch error:', error);

        // Try to create profile if it doesn't exist
        if (error.code === 'PGRST116') { // No rows returned
          // console.log('Creating new profile...');
          // console.log('authUser.user_metadata:', authUser.user_metadata);
          
          // Try to get stored registration data
          const storedData = localStorage.getItem('pendingProfileData');
          const registrationData = storedData ? JSON.parse(storedData) : {};
          // console.log('Stored registration data:', registrationData);
          
          const createTimeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Profile creation timeout')), 5000);
          });

          const createPromise = api.createProfile({
            id: authUser.id,
            first_name: authUser.user_metadata?.first_name || authUser.user_metadata?.firstName || registrationData.firstName || '',
            last_name: authUser.user_metadata?.last_name || authUser.user_metadata?.lastName || registrationData.lastName || '',
            username: authUser.user_metadata?.username || registrationData.username || authUser.email?.split('@')[0] || '',
            about: authUser.user_metadata?.about || registrationData.about || '',
            notifications: authUser.user_metadata?.notifications || registrationData.notifications || [],
            picture_url: authUser.user_metadata?.picture_url || authUser.user_metadata?.pictureUrl || registrationData.pictureUrl || 'https://i.ibb.co/DzFMwbR/default.png'
          });
          const { error: insertError } = await Promise.race([createPromise, createTimeoutPromise]);
          if (insertError) {
            console.error('Profile creation error:', insertError);
            setLoading(false);
            return;
          }

          // Fetch the newly created profile
          const fetchTimeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('New profile fetch timeout')), 5000);
          });

          const fetchPromise = api.getProfile(authUser.id);
          const { data: newProfile, error: fetchError } = await Promise.race([fetchPromise, fetchTimeoutPromise]);
          if (fetchError) {
            console.error('Failed to fetch new profile:', fetchError);
            setLoading(false);
            return;
          }
          profile = newProfile;
        } else {
          console.error('Unexpected profile fetch error:', error);
          setLoading(false);
          // Transient failure (cold start / network blip) — retry a few times
          // instead of leaving the UI stuck without a profile.
          if (attempt < 3) {
            setTimeout(() => loadUserProfile(authUser, attempt + 1), 1500 * attempt);
          }
          return;
        }
      }

      // console.log('Profile loaded successfully:', profile);
      
      // Perform daily reset only when the server says a new day has started.
      // Keyed on profile.last_reset_date (DB) instead of localStorage so the
      // reset cannot re-run on a second device the same day.
      const today = new Date().toISOString().split('T')[0];
      if (!profile.last_reset_date || profile.last_reset_date < today) {
        console.log('Date changed (server last_reset_date:', profile.last_reset_date, '), performing daily reset');
        api.dailyResetUser().catch(error => {
          console.error('Daily reset failed:', error);
        });
        profile = { ...profile, last_reset_date: today };
      }
      
      // Try to get stored registration data
      const storedData = localStorage.getItem('pendingProfileData');
      const registrationData = storedData ? JSON.parse(storedData) : {};
      
      // Check if profile is incomplete and try to update it
      if (!profile.first_name || !profile.last_name || !profile.username) {
        // console.log('Profile appears incomplete, attempting to update...');
        
        const updateData = {
          first_name: profile.first_name || authUser.user_metadata?.first_name || authUser.user_metadata?.firstName || registrationData.firstName || '',
          last_name: profile.last_name || authUser.user_metadata?.last_name || authUser.user_metadata?.lastName || registrationData.lastName || '',
          username: profile.username || authUser.user_metadata?.username || registrationData.username || authUser.email?.split('@')[0] || '',
          about: profile.about || authUser.user_metadata?.about || registrationData.about || '',
          picture_url: profile.picture_url || authUser.user_metadata?.picture_url || authUser.user_metadata?.pictureUrl || registrationData.pictureUrl || 'https://i.ibb.co/DzFMwbR/default.png'
        };
        
        const { error: updateError } = await api.updateProfile(authUser.id, updateData);
        
        if (!updateError) {
          profile = { ...profile, ...updateData };
          console.log('Profile updated with missing data:', profile);
        } else {
          console.error('Failed to update profile:', updateError);
        }
      }
      
      // Check if email is confirmed and add verified badge
      if (authUser.email_confirmed_at && (!profile.badges || !profile.badges.includes('verified'))) {
        console.log('Email confirmed, adding verified badge');
        const updatedBadges = profile.badges ? [...profile.badges] : [];
        if (!updatedBadges.includes('verified')) {
          updatedBadges.push('verified');
        }
        
        const { error: badgeError } = await api.updateProfile(authUser.id, { badges: updatedBadges });
        
        if (!badgeError) {
          profile.badges = updatedBadges;
          console.log('Verified badge added to profile');
        } else {
          console.error('Failed to add verified badge:', badgeError);
        }
      }
      
      // Check if profile is missing welcome notifications and add them
      if (profile && (!profile.notifications || profile.notifications.length === 0)) {
        const notificationsToAdd = authUser.user_metadata?.notifications || registrationData.notifications || [];
        if (notificationsToAdd.length > 0) {
          const { error: updateError } = await api.updateProfile(authUser.id, { notifications: notificationsToAdd });
          if (!updateError) {
            profile.notifications = notificationsToAdd;
            console.log('Added welcome notifications to profile');
          } else {
            console.error('Failed to add welcome notifications:', updateError);
          }
        }
      }
      
      // Update user with profile data
      const userWithProfile = {
        ...authUser,
        profile: profile
      };
      updateUser(userWithProfile);
      lastFetchedUserIdRef.current = authUser.id;
      // console.log('User profile loading completed');
      
      // Clean up stored registration data
      localStorage.removeItem('pendingProfileData');
    } catch (error) {
      console.error('Unexpected error loading profile:', error);
      // Don't set loading to false here since it's running in background.
      // Retry transient failures so the profile isn't stuck unloaded.
      if (attempt < 3) {
        setTimeout(() => loadUserProfile(authUser, attempt + 1), 1500 * attempt);
      }
    }
  };

  const signUp = async (email, password, metadata = {}) => {
    // Store registration metadata in localStorage for later use
    localStorage.setItem('pendingProfileData', JSON.stringify(metadata));
    
    const { data, error } = await api.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/email-verified`,
        data: {
          username: metadata.username,
          first_name: metadata.firstName,
          last_name: metadata.lastName,
          about: metadata.about,
          notifications: metadata.notifications,
          picture_url: metadata.pictureUrl
        }
      }
    });

    if (!error && data?.session?.user) {
      updateUser(data.session.user);
      loadUserProfile(data.session.user).catch(err => {
        console.error('Background profile loading failed:', err);
      });
    }

    return { data, error };
  };

  const signIn = async (email, password) => {
    const { data, error } = await api.signInWithPassword({
      email,
      password,
    });
    if (!error && data.session?.user) {
      updateUser(data.session.user);
      // Load profile in background (handles the daily reset via last_reset_date)
      loadUserProfile(data.session.user).catch(error => {
        console.error('Background profile loading failed:', error);
      });
    }
    return { data, error };
  };

  const signOut = async () => {
    try {
      const { error } = await api.signOut();
      if (error) {
        console.error('Sign out error:', error);
        // If logout fails (e.g., invalid session), still clear local state
        updateUser(null);
        setLoading(false);
      }
      // Always clear local state on logout attempt
      updateUser(null);
      setLoading(false);
    } catch (error) {
      console.error('Sign out error:', error);
      // Clear local state even if logout fails
      updateUser(null);
      setLoading(false);
    }
  };

  const getToken = async () => {
    try {
      const { data: { session }, error } = await api.getSession();
      if (error) {
        console.error('Error getting session:', error);
        return null;
      }
      return session?.access_token || null;
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  };

  const signInWithGoogle = async (redirectTo) => {
    const { data, error } = await api.signInWithOAuth({
      provider: 'google',
      redirectTo: redirectTo || `${window.location.origin}/auth/callback`
    });
    if (error || !data?.url) {
      console.error('Google sign-in error:', error || 'No auth URL returned');
      throw new Error(error?.message || 'Could not start Google sign-in. Please try again.');
    }
    window.location.href = data.url;
  };

  const reloadProfile = async (authUser) => {
    if (!authUser) return;
    
    try {
      const { data: profile, error } = await api.getProfile(authUser.id);

      if (error) {
        console.error('Error reloading profile:', error);
        return;
      }

      updateUser(prevUser => ({
        ...prevUser,
        profile: profile
      }));
    } catch (error) {
      console.error('Error reloading profile:', error);
    }
  };

  const value = useMemo(() => ({
    user,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    getToken,
    reloadProfile,
    updateUser,
  }), [user, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};